import React from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';

interface RouteErrorBoundaryProps {
  children: React.ReactNode;
}

interface RouteErrorBoundaryState {
  hasError: boolean;
}

export default class RouteErrorBoundary extends React.Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  state: RouteErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): RouteErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: unknown): void {
    if (typeof window !== 'undefined' && import.meta.env.DEV) {
      console.error('Route chunk failed to load:', error);
    }
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <Box
          role="alert"
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
          }}
        >
          <Stack spacing={2} sx={{ maxWidth: 480, textAlign: 'center', alignItems: 'center' }}>
            <Alert severity="warning" variant="outlined" sx={{ width: '100%' }}>
              <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Failed to load this page</Typography>
              <Typography variant="body2">
                Check your connection and reload — a newer version may be available.
              </Typography>
            </Alert>
            <Button variant="contained" onClick={this.handleReload}>
              Reload
            </Button>
          </Stack>
        </Box>
      );
    }
    return this.props.children;
  }
}
